import { useCallback, useRef, useState } from 'react';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import GradientDivider from 'pages/Documents/components/SuggestedDocumentReference/GradientDivider';
import {
  expandedHeaderSx,
  expandedIconSx,
  expandedPaperSx,
  expandedTitleSx,
} from 'pages/Documents/components/SuggestedDocumentReference/SuggestedDocumentReference.styles';
import SuggestionActionButtons from 'pages/Documents/components/SuggestedDocumentReference/SuggestionActionButtons';
import { AIBasePopoverProps } from './AIBasePopover.props';

const popperSx = { zIndex: 1300 };

const HOVER_CLOSE_DELAY = 500;
const CLICK_CLOSE_DELAY = 500;

const AIBasePopover = ({
  title,
  subtitle,
  acceptLabel = 'Accept',
  declineLabel = 'Dismiss',
  onAccept,
  onDecline,
  onRemove,
  removeLabel = 'Remove',
  expandedBody,
  trigger = 'click',
  children,
}: AIBasePopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | { getBoundingClientRect: () => DOMRect } | null>(null);
  const expandedRef = useRef(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Unique id for aria-describedby
  const idRef = useRef(`ai-popover-${Math.random().toString(36).slice(2)}`);
  const popperId = anchorEl ? idRef.current : undefined;

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    clearCloseTimeout();
    setAnchorEl(null);
    expandedRef.current = false;
  }, [clearCloseTimeout]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (trigger === 'hover') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      clearCloseTimeout();

      const rect = event.currentTarget.getBoundingClientRect();
      setAnchorEl(prev => {
        if (prev) {
          return null;
        }
        return { getBoundingClientRect: () => rect };
      });
    },
    [trigger, clearCloseTimeout]
  );

  const scheduleDelayedClose = useCallback(() => {
    const delay = trigger === 'hover' ? HOVER_CLOSE_DELAY : CLICK_CLOSE_DELAY;
    closeTimeout.current = setTimeout(() => {
      setAnchorEl(null);
      expandedRef.current = false;
    }, delay);
  }, [trigger]);

  const handlePopperMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handlePopperMouseLeave = useCallback(() => {
    clearCloseTimeout();
    scheduleDelayedClose();
  }, [clearCloseTimeout, scheduleDelayedClose]);

  const handleAnchorMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (trigger !== 'hover') {
        return;
      }
      clearCloseTimeout();
      setAnchorEl(event.currentTarget);
    },
    [trigger, clearCloseTimeout]
  );

  const handleAnchorMouseLeave = useCallback(() => {
    if (trigger !== 'hover') {
      return;
    }
    clearCloseTimeout();
    scheduleDelayedClose();
  }, [trigger, clearCloseTimeout, scheduleDelayedClose]);

  const noopMouseEnter = useCallback((_event: React.MouseEvent<HTMLElement>) => {}, []);
  const noopMouseLeave = useCallback(() => {}, []);

  const handleAccept = useCallback(() => {
    handleClose();
    onAccept?.();
  }, [handleClose, onAccept]);

  const handleDecline = useCallback(() => {
    handleClose();
    onDecline?.();
  }, [handleClose, onDecline]);

  const handleRemove = useCallback(() => {
    handleClose();
    onRemove?.();
  }, [handleClose, onRemove]);

  const hasActions = !!onAccept && !!onDecline;
  const hasRemoveAction = !hasActions && !!onRemove;

  const open = Boolean(anchorEl);

  return (
    <>
      {children({
        onMouseEnter: trigger === 'hover' ? handleAnchorMouseEnter : noopMouseEnter,
        onMouseLeave: trigger === 'hover' ? handleAnchorMouseLeave : noopMouseLeave,
        onClick: handleClick,
        ariaDescribedBy: popperId,
      })}

      <Popper
        id={popperId}
        open={open}
        anchorEl={anchorEl as any}
        placement="bottom"
        modifiers={[
          { name: 'offset', options: { offset: [0, 4] } },
          { name: 'flip', options: { fallbackPlacements: ['top', 'top-end'] } },
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
        ]}
        popperOptions={{ strategy: 'fixed' }}
        sx={popperSx}>
        <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown">
          <Paper
            onMouseEnter={handlePopperMouseEnter}
            onMouseLeave={handlePopperMouseLeave}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            elevation={4}
            sx={expandedPaperSx}>
            {/* Header */}
            <Box sx={expandedHeaderSx}>
              <Box component="img" src="/images/scalar_ai.png" alt="Scalar AI" sx={expandedIconSx} />
              <Box>
                <Typography sx={expandedTitleSx}>{title}</Typography>
                {subtitle && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            {expandedBody && (
              <>
                <GradientDivider />
                {expandedBody}
              </>
            )}

            {(hasActions || hasRemoveAction) && (
              <>
                <GradientDivider />

                <SuggestionActionButtons
                  acceptLabel={acceptLabel}
                  dismissLabel={declineLabel}
                  onAccept={hasActions ? handleAccept : undefined}
                  onDecline={hasActions ? handleDecline : undefined}
                  removeLabel={removeLabel}
                  onRemove={hasRemoveAction ? handleRemove : undefined}
                  centered
                />
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default AIBasePopover;
